import React from 'react';
import * as echarts from 'echarts/core';
import {
    PieChart as EChartsPieChart,
} from 'echarts/charts';
import {
    TooltipComponent,
    LegendComponent,
    TitleComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import EchartsWrapper from '@/components/echartsWrapper';

// 注册必要的组件
echarts.use([
    EChartsPieChart,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    CanvasRenderer,
]);

interface PieChartData {
    name: string;
    value: number;
}

interface PieChartProps {
    /** 图表数据 */
    data?: PieChartData[];
    /** 图表标题 */
    title?: string;
    /** 图表样式 */
    style?: React.CSSProperties;
    /** 是否为环形图 */
    isRing?: boolean;
    /** 饼图半径 - 饼图时使用字符串，环形图时使用数组 */
    radius?: string | [string, string];
    /** 图例位置 */
    legendPosition?: 'left' | 'right' | 'top' | 'bottom';
    /** 图例方向 */
    legendOrient?: 'horizontal' | 'vertical';
    /** 是否显示图例 */
    showLegend?: boolean;
    /** 是否显示数值标签 */
    showLabel?: boolean;
    /** 标签位置 */
    labelPosition?: 'outside' | 'inside' | 'center';
    /** 自定义颜色 */
    colors?: string[];
    /** 是否启用选中效果 */
    selectedMode?: boolean | 'single' | 'multiple';
    /** 中心位置 */
    center?: [string, string];
}

const PieChart: React.FC<PieChartProps> = ({
    data = [
        { name: '示例数据1', value: 335 },
        { name: '示例数据2', value: 310 },
        { name: '示例数据3', value: 234 },
    ],
    title = '饼状图',
    style = { width: '100%', height: '400px' },
    isRing = false,
    radius,
    legendPosition = 'right',
    legendOrient = 'vertical',
    showLegend = true,
    showLabel = true,
    labelPosition = 'outside',
    colors = [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
        '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#fab1a0'
    ],
    selectedMode = false,
    center = ['50%', '50%']
}) => {
    // 根据是否为环形图确定半径
    const getRadius = () => {
        if (radius) {
            return radius;
        }
        return isRing ? ['40%', '70%'] : '70%';
    };

    const chartOption = {
        title: {
            text: title,
            left: 'center',
            top: '5%',
            textStyle: {
                fontSize: 16,
                fontWeight: 'bold'
            }
        },
        tooltip: {
            trigger: 'item',
            formatter: function (params: any) {
                return `${params.seriesName}<br/>${params.name}: ${params.value} (${params.percent}%)`;
            }
        },
        legend: showLegend ? {
            orient: legendOrient,
            left: legendPosition === 'left' ? 'left' :
                legendPosition === 'right' ? 'right' : 'center',
            top: legendPosition === 'top' ? 'top' :
                legendPosition === 'bottom' ? 'bottom' : 'middle',
            data: data.map(item => item.name),
            textStyle: {
                fontSize: 12
            }
        } : undefined,
        color: colors,
        series: [
            {
                name: title,
                type: 'pie',
                radius: getRadius(),
                center: center,
                data: data,
                selectedMode: selectedMode,
                label: {
                    show: showLabel,
                    position: labelPosition,
                    formatter: function (params: any) {
                        if (labelPosition === 'center') {
                            return `{name|${params.name}}\n{value|${params.value}}`;
                        }
                        return `{name|${params.name}}\n{value|${params.value}} ({percent|${params.percent}%})`;
                    },
                    rich: {
                        name: {
                            fontSize: 14,
                            fontWeight: 'bold',
                            color: '#333'
                        },
                        value: {
                            fontSize: 16,
                            fontWeight: 'bold',
                            color: '#666'
                        },
                        percent: {
                            fontSize: 12,
                            color: '#999'
                        }
                    }
                },
                labelLine: {
                    show: showLabel && labelPosition === 'outside'
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                },
                animationType: 'scale',
                animationEasing: 'elasticOut',
                animationDelay: function (idx: number) {
                    return Math.random() * 200;
                }
            }
        ]
    };

    return (
        <>
            {data && data.length > 0 ? (
                <EchartsWrapper
                    chartOption={chartOption}
                    style={style}
                />
            ) : (
                <div style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px'
                }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#666' }}>{title}</h4>
                    <p style={{ margin: 0, color: '#999' }}>暂无数据</p>
                </div>
            )}
        </>
    );
};

export default PieChart;